import React, { useEffect, useState } from "react";
import { Dialog, TextField } from "@mui/material";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { Spinner } from "../../components/UI/States";
import { Notice } from "../../components/Fleet/FleetParts";
import { assignTraining, fetchAssignableRoles } from "./safetyApi";
import { DialogHeader, PeoplePicker, plural } from "./SafetyParts";

// Assign a training video to roles and/or people, with an optional due date.
// Server: POST /api/safety/training/:id/assign — mirrors the app's assign,
// so a role keeps being assigned to new members by the app's daily job.

// Same choices as the app's assign screen, plus a date picker.
const DUE_PRESETS = [
  { key: "none", label: "No due date", days: null },
  { key: "3", label: "3 days", days: 3 },
  { key: "7", label: "1 week", days: 7 },
  { key: "14", label: "2 weeks", days: 14 },
  { key: "30", label: "30 days", days: 30 },
  { key: "date", label: "Pick a date" },
];

// End of the viewer's local day, `days` from today.
const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 0);
  return d;
};
const daysFromNow = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return endOfDay(d);
};
const todayKey = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const ToggleChip = ({ active, onClick, children }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={`rounded-full border px-3 py-1 text-sm font-medium transition ${
      active
        ? "border-transparent bg-[color:var(--app-accent-solid)] text-white"
        : "app-divider hover:bg-[color:var(--app-hover)]"
    }`}
  >
    {children}
  </button>
);

const AssignTrainingDialog = ({ open, video, userId, onClose, onAssigned }) => {
  const [roles, setRoles] = useState(null);
  const [pickedRoles, setPickedRoles] = useState([]);
  const [people, setPeople] = useState([]);
  const [due, setDue] = useState("14");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPickedRoles([]);
    setPeople([]);
    setDue("14");
    setDueDate("");
    setSaving(false);
    setError(null);
    setResult(null);
    if (!roles) {
      fetchAssignableRoles(userId)
        .then((data) => setRoles(data.roles))
        .catch((e) => setError(e.message));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRole = (role) =>
    setPickedRoles((list) => (list.includes(role) ? list.filter((r) => r !== role) : [...list, role]));
  const addPerson = (p) => setPeople((list) => (list.some((x) => x.email === p.email) ? list : [...list, p]));
  const removePerson = (email) => setPeople((list) => list.filter((p) => p.email !== email));

  const dueAt = (() => {
    if (due === "date") return dueDate ? endOfDay(`${dueDate}T00:00:00`) : null;
    const preset = DUE_PRESETS.find((p) => p.key === due);
    return preset?.days ? daysFromNow(preset.days) : null;
  })();
  const roleHeadcount = (roles || [])
    .filter((r) => pickedRoles.includes(r.role))
    .reduce((sum, r) => sum + r.people, 0);
  const canSubmit = (pickedRoles.length || people.length) && !(due === "date" && !dueDate) && !saving;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await assignTraining(userId, video.id, {
        roles: pickedRoles,
        people: people.map((p) => p.email),
        dueAt: dueAt ? dueAt.toISOString() : null,
      });
      setResult(res);
      onAssigned?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { m: 2 } }}
      aria-labelledby="assign-training-title"
    >
      <div className="p-5 sm:p-6">
        <DialogHeader
          id="assign-training-title"
          icon={AssignmentIndRoundedIcon}
          tone="violet"
          title="Assign training"
          subtitle={video?.title}
          onClose={onClose}
          closeDisabled={saving}
        />

        {result ? (
          <div className="mt-5">
            <p className="flex items-start gap-2 text-sm font-semibold">
              <CheckCircleRoundedIcon className="text-emerald-600 dark:text-emerald-400" fontSize="small" />
              {result.newlyAssigned
                ? `Assigned to ${plural(result.newlyAssigned, "new person", "new people")}.`
                : "Everyone you picked already had it."}
            </p>
            <p className="app-muted mt-2 text-sm">
              {result.alreadyAssigned > 0 &&
                `${plural(result.alreadyAssigned, "person", "people")} already had it${
                  dueAt ? " — their due date was updated" : ""
                }. `}
              {result.newlyAssigned > 0 &&
                "New assignees are notified on the website and, if they use the Big C mobile app, on their phone. "}
              {pickedRoles.length > 0 &&
                `Anyone who gets the ${pickedRoles.join(", ")} role later is assigned automatically.`}
            </p>
            <div className="mt-5 flex justify-end">
              <button type="button" className="app-btn app-btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-5">
            <section>
              <p className="app-eyebrow mb-1">Roles</p>
              <p className="app-muted mb-2 text-xs">
                Everyone with the role gets it — and so does anyone who gets the role later.
              </p>
              {!roles && !error && <Spinner size={18} />}
              {roles && (
                <div className="flex flex-wrap gap-1.5">
                  {roles.map((r) => (
                    <ToggleChip key={r.role} active={pickedRoles.includes(r.role)} onClick={() => toggleRole(r.role)}>
                      {r.role} <span className="tabular-nums opacity-70">{r.people}</span>
                    </ToggleChip>
                  ))}
                </div>
              )}
            </section>

            <section>
              <p className="app-eyebrow mb-2">People</p>
              <PeoplePicker userId={userId} people={people} onAdd={addPerson} onRemove={removePerson} />
            </section>

            <section>
              <p className="app-eyebrow mb-2">Due date</p>
              <div className="flex flex-wrap gap-1.5">
                {DUE_PRESETS.map((p) => (
                  <ToggleChip key={p.key} active={due === p.key} onClick={() => setDue(p.key)}>
                    {p.label}
                  </ToggleChip>
                ))}
              </div>
              {due === "date" && (
                <TextField
                  type="date"
                  size="small"
                  label="Due on"
                  className="mt-3"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: todayKey() }}
                />
              )}
              {dueAt && (
                <p className="app-muted mt-2 text-xs">
                  Due{" "}
                  {dueAt.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" })}. The
                  mobile app sends a reminder each day starting 2 days before it's due, until it's done.
                </p>
              )}
            </section>

            {error && <Notice tone="rose">{error}</Notice>}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t app-divider pt-4">
              <span className="app-muted text-xs">
                {pickedRoles.length || people.length
                  ? `Up to ${plural(roleHeadcount + people.length, "person", "people")}${
                      pickedRoles.length && people.length ? " (some may overlap)" : ""
                    }`
                  : "Pick a role or a person."}
              </span>
              <span className="flex gap-2">
                <button type="button" className="app-btn app-btn-ghost" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="app-btn app-btn-primary" onClick={submit} disabled={!canSubmit}>
                  {saving ? "Assigning…" : "Assign"}
                </button>
              </span>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default AssignTrainingDialog;
